import React from 'react';
import gql from 'graphql-tag';
import { useQuery, useMutation, useApolloClient } from '@apollo/react-hooks';

import { useTranslation } from 'hooks/useTranslation';
import { pluralize } from 'helpers/string';
import { DashboardHeading, Loader, Button, Modal, DefaultModalContent } from 'components';
import { SettingsIntegrationIcon } from './';
import { refreshOrgIntegrationsQuery } from 'services/apollo/queries';
import { Logger } from 'services';

import styles from './OrganizationApps.module.scss';

const getOrganizationAppsTabQuery = gql`
  query getOrganizationAppsTab {
    me {
      organization {
        id
        usage {
          totalIntegrationsCount
          integrations {
            title
            type
            userCount
          }
        }
      }
    }
  }
`;

const deleteOrgIntegrationMutation = gql`
  mutation deleteOrgIntegration($integrationType: String!) {
    deleteOrgIntegration(integrationType: $integrationType) {
      ok
      error
    }
  }
`;

export const OrganizationApps = () => {
  const { t } = useTranslation();
  const client = useApolloClient();

  const [modalIsOpen, setIsOpen] = React.useState(false);
  const [integrationToRemove, setIntegrationToRemove] = React.useState({});
  const [deleteOrgIntegration] = useMutation(deleteOrgIntegrationMutation);

  const openModal = () => {
    setIsOpen(true);
  };

  const closeModal = () => {
    setIsOpen(false);
  };
  const { error, data } = useQuery(getOrganizationAppsTabQuery, {
    fetchPolicy: 'cache-and-network',
  });

  if (data === undefined) return <Loader />;

  if (error) return <Loader color="Red" />;

  const usage = data?.me?.organization?.usage;
  return (
    <div className={styles.container}>
      <DashboardHeading className={styles.heading} level={1}>
        {t('settings-screen.organization-apps.connected-apps')}{' '}
        {`(${usage?.totalIntegrationsCount ? usage?.totalIntegrationsCount : 0})`}
      </DashboardHeading>
      <div>
        {usage?.integrations.map((integration: any) => {
          const [key] = integration.type.split('@');
          return (
            <div className={styles.app} key={integration.type}>
              <SettingsIntegrationIcon itemIntegrationName={key} />
              <div className={styles.appMetaWrap}>
                <div className={styles.appTitle}>{integration.title}</div>
                <div className={styles.appCount}>
                  {integration.userCount}{' '}
                  {pluralize(integration.userCount, t('settings-screen.organization-apps.account'))}{' '}
                  {t('settings-screen.organization-apps.connected')}
                </div>
              </div>
              <Button
                className={styles.appButton}
                type="Plain"
                onClick={() => {
                  setIntegrationToRemove(integration);
                  openModal();
                }}
              >
                {t('settings-screen.organization-apps.disconnect')}
              </Button>
            </div>
          );
        })}
      </div>
      <Modal isOpen={modalIsOpen} onRequestClose={closeModal}>
        <DefaultModalContent
          title={`${t('settings-screen.organization-apps.disconnect')} ${
            //@ts-ignore
            integrationToRemove.title
          }`}
          subtext={t('settings-screen.organization-apps.disconnect-subtext')}
          cancelButtonText={t('settings-screen.organization-apps.no')}
          onCancel={closeModal}
          actionButtonText={t('settings-screen.organization-apps.yes')}
          onAction={() => {
            //@ts-ignore
            deleteOrgIntegration({ variables: { integrationType: integrationToRemove.type } }).then(
              (data: any) => {
                const {
                  data: {
                    deleteOrgIntegration: { error, ok },
                  },
                } = data;

                if (error) {
                  // TODO Add error logging
                  Logger.log(error);
                }

                if (ok) {
                  client
                    .query({
                      query: refreshOrgIntegrationsQuery,
                      fetchPolicy: 'network-only',
                    })
                    .then(() => {
                      closeModal();
                    })
                    .catch((error) => {
                      Logger.error(error);
                    });
                }
              }
            );
          }}
        />
      </Modal>
    </div>
  );
};
